import React, { useState } from 'react';
import {
    Drawer,
    Typography,
    Space,
    Badge,
    Tag,
    Card,
    Avatar,
    Button,
    Input,
    message,
    Tooltip,
    Row,
    Col,
    Descriptions,
    Timeline,
    Spin,
    Empty,
    Modal,
    Upload,
    List,
} from 'antd';
import {
    UserOutlined,
    ClockCircleOutlined,
    CheckCircleOutlined,
    ExclamationCircleOutlined,
    SendOutlined,
    PaperClipOutlined,
    DownloadOutlined,
} from '@ant-design/icons';
import {
    useGetTicketQuery,
    useAddTicketCommentMutation,
    useUpdateTicketMutation,
    useUploadTicketAttachmentMutation,
} from '../../services/nestjs-base-api';
import { useAuth } from '../../hooks/useAuth';
import type { ITicket } from '../../services/nestjs-base-api/types';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useIntl } from 'react-intl';

dayjs.extend(relativeTime);

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

interface CustomerTicketDetailDrawerProps {
    ticketHash: string | null;
    visible: boolean;
    onClose: () => void;
}

export const CustomerTicketDetailDrawer: React.FC<CustomerTicketDetailDrawerProps> = ({
    ticketHash,
    visible,
    onClose,
}) => {
    const { user } = useAuth();
    const intl = useIntl();
    const [commentText, setCommentText] = useState('');

    // Status badge component (moved inside to access intl)
    const TicketStatusBadge: React.FC<{ status: ITicket['status'] }> = ({ status }) => {
        const statusConfig = {
            open: { color: 'blue', icon: <ExclamationCircleOutlined />, text: intl.formatMessage({ id: 'support.status.open' }) },
            in_progress: { color: 'processing', icon: <ClockCircleOutlined />, text: intl.formatMessage({ id: 'support.status.inProgress' }) },
            waiting_on_customer: { color: 'warning', icon: <ClockCircleOutlined />, text: intl.formatMessage({ id: 'support.status.waitingOnYou' }) },
            resolved: { color: 'success', icon: <CheckCircleOutlined />, text: intl.formatMessage({ id: 'support.status.resolved' }) },
            closed: { color: 'default', icon: <CheckCircleOutlined />, text: intl.formatMessage({ id: 'support.status.closed' }) },
        };

        const config = statusConfig[status];
        return (
            <Badge
                status={config.color as any}
                text={
                    <Space size={4}>
                        {config.icon}
                        {config.text}
                    </Space>
                }
            />
        );
    };

    // Priority badge component (moved inside to access intl)
    const TicketPriorityBadge: React.FC<{ priority: ITicket['priority'] }> = ({ priority }) => {
        const priorityConfig = {
            low: { color: 'default', text: intl.formatMessage({ id: 'support.priority.low' }) },
            normal: { color: 'blue', text: intl.formatMessage({ id: 'support.priority.normal' }) },
            high: { color: 'orange', text: intl.formatMessage({ id: 'support.priority.high' }) },
            urgent: { color: 'red', text: intl.formatMessage({ id: 'support.priority.urgent' }) },
        };

        const config = priorityConfig[priority];
        return <Tag color={config.color}>{config.text}</Tag>;
    };

    // Fetch ticket data
    const {
        data: ticket,
        isLoading,
        refetch,
    } = useGetTicketQuery(
        { jwtToken: user?.jwtToken || '', hash: ticketHash || '' },
        { skip: !ticketHash || !visible },
    );

    // Mutations
    const [addComment, { isLoading: isAddingComment }] = useAddTicketCommentMutation();
    const [updateTicket] = useUpdateTicketMutation();
    const [uploadAttachment, { isLoading: isUploading }] = useUploadTicketAttachmentMutation();

    // Handle status change
    const handleStatusChange = async (newStatus: 'resolved' | 'closed') => {
        if (!ticket) return;

        Modal.confirm({
            title: intl.formatMessage({ id: 'support.detail.closeTicket.title' }),
            content: intl.formatMessage({
                id: newStatus === 'resolved'
                    ? 'support.detail.closeTicket.resolvedConfirm'
                    : 'support.detail.closeTicket.closedConfirm'
            }),
            okText: intl.formatMessage({ id: 'support.detail.closeTicket.ok' }),
            cancelText: intl.formatMessage({ id: 'support.detail.closeTicket.cancel' }),
            onOk: async () => {
                try {
                    await updateTicket({
                        jwtToken: user?.jwtToken || '',
                        hash: ticket.hash,
                        data: { status: newStatus },
                    }).unwrap();
                    message.success(intl.formatMessage({ id: 'support.detail.closeTicket.success' }));
                    refetch();
                } catch (error) {
                    message.error(intl.formatMessage({ id: 'support.detail.closeTicket.error' }));
                }
            },
        });
    };

    // Handle add comment
    const handleAddComment = async () => {
        if (!ticket || !commentText.trim()) return;

        try {
            await addComment({
                jwtToken: user?.jwtToken || '',
                hash: ticket.hash,
                data: {
                    content: commentText,
                },
            }).unwrap();
            message.success(intl.formatMessage({ id: 'support.detail.comment.success' }));
            setCommentText('');
            refetch();
        } catch (error) {
            message.error(intl.formatMessage({ id: 'support.detail.comment.error' }));
        }
    };

    // Handle file upload
    const handleFileUpload = async (file: File) => {
        if (!ticket) return false;

        try {
            await uploadAttachment({
                jwtToken: user?.jwtToken || '',
                hash: ticket.hash,
                file,
            }).unwrap();
            message.success(intl.formatMessage({ id: 'support.detail.attachment.uploadSuccess' }));
            refetch();
            return false; // Prevent default upload behavior
        } catch (error) {
            message.error(intl.formatMessage({ id: 'support.detail.attachment.uploadError' }));
            return false;
        }
    };

    // Handle file download
    const handleFileDownload = async (attachmentHash: string, filename: string) => {
        try {
            // Get API base URL and remove trailing slash if present
            let apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3501';
            apiBaseUrl = apiBaseUrl.replace(/\/$/, '');

            const url = `${apiBaseUrl}/tickets/attachments/${attachmentHash}`;

            console.log('API Base URL:', apiBaseUrl);
            console.log('Downloading from:', url);

            const response = await fetch(url, {
                headers: {
                    Authorization: `Bearer ${user?.jwtToken}`,
                },
            });

            console.log('Response status:', response.status);

            if (!response.ok) {
                const errorText = await response.text();
                console.error('Download failed:', errorText);
                throw new Error(`Download failed: ${response.status}`);
            }

            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(downloadUrl);
            document.body.removeChild(a);

            message.success(intl.formatMessage({ id: 'support.detail.attachment.downloadSuccess' }));
        } catch (error: any) {
            console.error('Download error:', error);
            message.error(intl.formatMessage({ id: 'support.detail.attachment.downloadError' }) + `: ${error.message}`);
        }
    };

    // Render ticket metadata
    const renderTicketInfo = () => {
        if (!ticket) return null;

        return (
            <Card style={{ marginBottom: 16 }}>
                <Descriptions column={2} size="small">
                    <Descriptions.Item label={intl.formatMessage({ id: 'support.detail.ticketId' })}>
                        <Text code copyable={{ text: ticket.hash }}>
                            {ticket.hash.substring(0, 12)}...
                        </Text>
                    </Descriptions.Item>
                    <Descriptions.Item label={intl.formatMessage({ id: 'support.detail.created' })}>
                        <Tooltip title={dayjs(ticket.createdAt).format('YYYY-MM-DD HH:mm:ss')}>
                            {dayjs(ticket.createdAt).fromNow()}
                        </Tooltip>
                    </Descriptions.Item>
                    {ticket.brand && (
                        <Descriptions.Item label={intl.formatMessage({ id: 'support.detail.brand' })}>
                            <Tag>{ticket.brand.name}</Tag>
                        </Descriptions.Item>
                    )}
                    {ticket.category && (
                        <Descriptions.Item label={intl.formatMessage({ id: 'support.detail.category' })}>
                            <Tag color={ticket.category.color || 'default'}>
                                {ticket.category.name}
                            </Tag>
                        </Descriptions.Item>
                    )}
                    {ticket.assignedToUser && (
                        <Descriptions.Item label={intl.formatMessage({ id: 'support.detail.assignedTo' })} span={2}>
                            <Space>
                                <UserOutlined />
                                {ticket.assignedToUser.firstName} {ticket.assignedToUser.lastName}
                            </Space>
                        </Descriptions.Item>
                    )}
                </Descriptions>
            </Card>
        );
    };

    // Render conversation timeline
    const renderConversation = () => {
        if (!ticket || !ticket.comments) return null;

        // Filter out internal comments (customers shouldn't see them)
        const visibleComments = ticket.comments.filter(c => !c.isInternal);

        const allItems = [
            {
                type: 'ticket',
                date: ticket.createdAt,
                user: ticket.createdByUser,
                content: ticket.description,
            },
            ...visibleComments.map((comment) => ({
                type: 'comment',
                date: comment.createdAt,
                user: comment.authorUser,
                content: comment.content,
            })),
        ].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

        return (
            <Timeline style={{ marginTop: 16 }}>
                {allItems.map((item, index) => (
                    <Timeline.Item
                        key={index}
                        color={item.type === 'ticket' ? 'blue' : 'green'}
                    >
                        <Card size="small">
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <Space style={{ justifyContent: 'space-between', width: '100%' }}>
                                    <Space>
                                        <Avatar icon={<UserOutlined />} size="small" />
                                        <Text strong>
                                            {item.user.firstName} {item.user.lastName}
                                        </Text>
                                        {item.type === 'ticket' && <Tag color="blue">{intl.formatMessage({ id: 'support.detail.tag.original' })}</Tag>}
                                        {item.type === 'comment' && item.user.id !== user?.id && (
                                            <Tag color="green">{intl.formatMessage({ id: 'support.detail.tag.support' })}</Tag>
                                        )}
                                    </Space>
                                    <Text type="secondary" style={{ fontSize: '12px' }}>
                                        {dayjs(item.date).format('MMM D, YYYY HH:mm')}
                                    </Text>
                                </Space>
                                <Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap', textAlign: 'left' }}>
                                    {item.content}
                                </Paragraph>
                            </Space>
                        </Card>
                    </Timeline.Item>
                ))}
            </Timeline>
        );
    };

    return (
        <Drawer
            title={ticket?.subject || intl.formatMessage({ id: 'support.detail.title' })}
            placement="right"
            width={800}
            onClose={onClose}
            open={visible}
        >
            <Spin spinning={isLoading}>
                {ticket && (
                    <Space direction="vertical" style={{ width: '100%' }} size="large">
                        {/* Ticket Info */}
                        {renderTicketInfo()}

                        {/* Status & Priority */}
                        <Card size="small">
                            <Space size="large">
                                <div>
                                    <Text type="secondary">{intl.formatMessage({ id: 'support.detail.statusLabel' })}</Text>
                                    <TicketStatusBadge status={ticket.status} />
                                </div>
                                <div>
                                    <Text type="secondary">{intl.formatMessage({ id: 'support.detail.priorityLabel' })}</Text>
                                    <TicketPriorityBadge priority={ticket.priority} />
                                </div>
                            </Space>
                        </Card>

                        {/* Conversation Thread */}
                        <Card title={intl.formatMessage({ id: 'support.detail.conversation' })} size="small">
                            {renderConversation()}
                        </Card>

                        {/* Attachments */}
                        {ticket.attachments && ticket.attachments.length > 0 && (
                            <Card title={intl.formatMessage({ id: 'support.detail.attachments.title' })} size="small">
                                <List
                                    size="small"
                                    dataSource={ticket.attachments}
                                    renderItem={(attachment) => (
                                        <List.Item
                                            key={attachment.id}
                                            actions={[
                                                <Tooltip title={intl.formatMessage({ id: 'support.detail.attachments.download' })}>
                                                    <Button
                                                        type="text"
                                                        size="small"
                                                        icon={<DownloadOutlined />}
                                                        onClick={() => handleFileDownload(attachment.hash, attachment.originalFilename)}
                                                    />
                                                </Tooltip>,
                                            ]}
                                        >
                                            <List.Item.Meta
                                                avatar={<PaperClipOutlined />}
                                                title={attachment.originalFilename}
                                                description={`${(attachment.fileSize / 1024).toFixed(2)} KB - ${intl.formatMessage({ id: 'support.detail.attachments.uploadedBy' })} ${attachment.uploadedByUser.firstName} ${attachment.uploadedByUser.lastName}`}
                                            />
                                        </List.Item>
                                    )}
                                />
                            </Card>
                        )}

                        {/* Upload New Attachments */}
                        {ticket.status !== 'closed' && (
                            <Card title={intl.formatMessage({ id: 'support.detail.attachments.addNew' })} size="small">
                                <Upload
                                    beforeUpload={handleFileUpload}
                                    showUploadList={false}
                                    multiple
                                    maxCount={5}
                                >
                                    <Button
                                        icon={<PaperClipOutlined />}
                                        loading={isUploading}
                                        disabled={isUploading}
                                    >
                                        {intl.formatMessage({ id: 'support.detail.attachments.uploadButton' })}
                                    </Button>
                                </Upload>
                                <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: '12px' }}>
                                    {intl.formatMessage({ id: 'support.detail.attachments.uploadHint' })}
                                </Text>
                            </Card>
                        )}

                        {/* Close Ticket Actions */}
                        {ticket.status !== 'closed' && ticket.status !== 'resolved' && (
                            <Card size="small">
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    <Text type="secondary">
                                        {intl.formatMessage({ id: 'support.detail.closeTicket.prompt' })}
                                    </Text>
                                    <Space>
                                        <Button
                                            icon={<CheckCircleOutlined />}
                                            onClick={() => handleStatusChange('resolved')}
                                        >
                                            {intl.formatMessage({ id: 'support.detail.closeTicket.markResolved' })}
                                        </Button>
                                        <Button
                                            icon={<CheckCircleOutlined />}
                                            onClick={() => handleStatusChange('closed')}
                                        >
                                            {intl.formatMessage({ id: 'support.detail.closeTicket.markClosed' })}
                                        </Button>
                                    </Space>
                                </Space>
                            </Card>
                        )}

                        {/* Add Comment Form */}
                        {ticket.status !== 'closed' && (
                            <Card title={intl.formatMessage({ id: 'support.detail.addComment' })} size="small">
                                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                                    <TextArea
                                        rows={4}
                                        value={commentText}
                                        onChange={(e) => setCommentText(e.target.value)}
                                        placeholder={intl.formatMessage({ id: 'support.detail.comment.placeholder' })}
                                        maxLength={5000}
                                    />
                                    <Row justify="space-between" align="middle">
                                        <Col>
                                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                                {commentText.length} / 5000
                                            </Text>
                                        </Col>
                                        <Col>
                                            <Button
                                                type="primary"
                                                icon={<SendOutlined />}
                                                onClick={handleAddComment}
                                                disabled={!commentText.trim()}
                                                loading={isAddingComment}
                                            >
                                                {intl.formatMessage({ id: 'support.detail.button.send' })}
                                            </Button>
                                        </Col>
                                    </Row>
                                </Space>
                            </Card>
                        )}

                        {ticket.status === 'closed' && (
                            <Card>
                                <Empty
                                    description={intl.formatMessage({ id: 'support.detail.closed.title' })}
                                >
                                    <Text type="secondary">
                                        {intl.formatMessage({ id: 'support.detail.closed.description' })}
                                    </Text>
                                </Empty>
                            </Card>
                        )}
                    </Space>
                )}
            </Spin>
        </Drawer>
    );
};

export default CustomerTicketDetailDrawer;
